import {
  CtaBehavior,
  CtaStyle,
  FontSettings,
  LengthUnitOfMeasurement,
  WidgetState,
} from '../types/widget.state';
import { getContrastedColor } from './color.helper';
import {
  setChestShape,
  setHipShape,
  setBellyShape,
} from './shape.features.helper';

function setLengthUnitCSS(lengthUnit: LengthUnitOfMeasurement) {
  if (lengthUnit === 'cm') {
    return `.height__in { font-size: 1rem; }
        .height__cm { font-size: 1.75rem; }`;
  } else if (lengthUnit === 'inches') {
    return `.height__in { font-size: 1.75rem; }
      .height__cm { font-size: 1rem; }`;
  } else if (lengthUnit === 'remove-cm') {
    return `.height__in { font-size: 1.75rem; }
      .height__cm { display: none; }`;
  }
  return `.height__in { display: none; }
        .height__cm { font-size: 1.75rem; }`;
}

function setCtaStyle(ctaStyle: CtaStyle) {
  const { borderRadius, backgroundColor, fontColor } = ctaStyle;
  return `.es-btn-primary {
        color: ${fontColor} !important;
        background-color: ${backgroundColor} !important;
        border-radius: ${borderRadius}px !important;
        border: none !important;
    }
      .es-btn-primary:hover {
        background-color: ${backgroundColor}CC !important;
      }

      /* This sets the style of an already selected option */
      .ant-radio-button-wrapper-checked {
        color: ${getContrastedColor(backgroundColor)} !important;
        background-color: ${backgroundColor} !important;
        border-color: ${backgroundColor} !important; 
      }
    `;
}

function setCtaBehavior(ctaBehavior: CtaBehavior) {
  if (ctaBehavior === 'cart') {
    return {
      hide_cart_button: false,
    };
  }
  return {
    hide_cart_button: true,
  };
}

function setWidgetBackground(background: string) {
  return `.card {
    background-color: ${background} !important;
  }`;
}

function showOrHideEasysizeButton(showEasysizeWatermark: boolean | undefined) {
  if (!showEasysizeWatermark) {
    return `
    .logo a, .es-info-btn {
      display: none;
    }
    `;
  }
  return '';
}

function setFontStyle(fontSetting: FontSettings) {
  const {
    headerFontColor,
    headerFontSize,
    headerFontWeight,
    headerFontOnlyCapitals,
    bodyFontColor,
    bodyFontSize,
    bodyFontWeight,
    bodyPadding,
    bodyBorderHasRadius,
    bodyFontOnlyCapitals,
    customBodyFontUrl,
    customBodyFontName,
    customHeaderFontUrl,
    customHeaderFontName,
  } = fontSetting;

  const customBodyFontFamily = () => {
    if (customBodyFontUrl && customBodyFontName) {
      return `
      @font-face {
        font-family: ${customBodyFontName};
        src: url(${customBodyFontUrl});
      }
      `;
    } else {
      return '';
    }
  };
  const customHeaderFontFamily = () => {
    if (customHeaderFontUrl && customHeaderFontName) {
      return `
      @font-face {
        font-family: ${customHeaderFontName};
        src: url(${customHeaderFontUrl});
        font-weight: bold;
      }
      `;
    } else {
      return '';
    }
  };

  return `
    ${customBodyFontFamily()}
    ${customHeaderFontFamily()}
    .ant-radio-button-wrapper p, .ant-radio-button-wrapper span, .es-result .es-btn-label {
      color: ${bodyFontColor} !important;
      font-size: ${bodyFontSize}px !important;
      text-transform: ${bodyFontOnlyCapitals ? 'uppercase' : 'none'} !important;
      font-weight: ${bodyFontWeight} !important;
      font-family: ${
        customBodyFontName
          ? customBodyFontName
          : "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;"
      };
    }

    .es-result .es-result-subtitle, .es-result .es-not-found-title, .es-box-title {
      font-family: ${
        customBodyFontName
          ? customBodyFontName
          : "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;"
      };
    }

    .es-option--single .ant-radio-button-wrapper, .es-option--single-tab .ant-radio-button-wrapper {
      padding: ${bodyPadding.vertical}px ${bodyPadding.horizontal}px!important;
      border-radius: ${bodyBorderHasRadius ? '4' : '0'}px !important;
    }

    .es-result .es-result-text .value, .es-result .es-result-text .label {
      font-family: ${
        customHeaderFontName
          ? customHeaderFontName
          : "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;"
      };
    }

    .question-wrapper h6, .es-result .es-result-title {
      color: ${headerFontColor} !important;
      font-size: ${headerFontSize}px !important;
      text-transform: ${
        headerFontOnlyCapitals ? 'uppercase' : 'none'
      } !important;
      font-weight: ${headerFontWeight} !important;
      font-family: ${
        customHeaderFontName
          ? customHeaderFontName
          : "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;"
      };
    }
    .ant-radio-button-wrapper-checked span {
      color: #fff !important;
    }
    .es-option--single .ant-radio-button-wrapper {
      line-height: 1.5rem;
      height: 2px !important;
    }
  `;
}

export function generateWidgetFeature(feature: WidgetState, shopId: string) {
  const displayESWatermark = showOrHideEasysizeButton(
    feature.showEasysizeWatermark
  );
  const fontStyle = setFontStyle(feature.fontSetting);
  const lengthUnit = setLengthUnitCSS(feature.lengthUnit);
  const ctaStyle = setCtaStyle(feature.ctaStyle);
  const widgetBackground = setWidgetBackground(feature.widgetBackground);
  const ctaBehavior = setCtaBehavior(feature.ctaBehavior);
  const chestShape = setChestShape(
    feature.maleChestShape,
    feature.femaleChestShape
  );
  const hipShape = setHipShape(feature.maleHipShape, feature.femaleHipShape);
  const bellyShape = setBellyShape(
    feature.maleBellyShape,
    feature.femaleBellyShape
  );

  const css = `
  ${displayESWatermark}
  ${fontStyle}
  ${lengthUnit}
  ${ctaStyle}
  ${widgetBackground}
`;

  const features = {
    ...ctaBehavior,
  };

  const questions = {
    ...chestShape,
    ...hipShape,
    ...bellyShape,
  };

  const result = {
    css,
    features: {
      features,
      questions,
    },
    shop_id: shopId,
  };

  return result;
}
